import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {IntersectionObserver | undefined} */
  #availabilityObserver;

  /** @type {Element | null} */
  #observedUnavailableItem = null;

  /** @type {MutationObserver | undefined} */
  #gridWatcher;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#watchGrid();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#availabilityObserver?.disconnect();
    this.#gridWatcher?.disconnect();
  }

  /**
   * Watches the grid for changes and keeps the availability sentinel updated.
   */
  #watchGrid() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#updateAvailabilitySentinel();

    this.#gridWatcher = new MutationObserver(() => {
      this.#updateAvailabilitySentinel();
    });
    this.#gridWatcher.observe(grid, { childList: true });
  }

  /**
   * Observes the first out-of-stock item in the grid.
   * When it is about to enter the viewport the next page is triggered,
   * so new in-stock products load before the user reaches unavailable ones.
   */
  #updateAvailabilitySentinel() {
    const { grid } = this.refs;
    if (!grid) return;

    const firstUnavailable = grid.querySelector('[data-available="false"]');

    // Nothing changed
    if (firstUnavailable === this.#observedUnavailableItem) return;

    // Stop observing the previous sentinel
    if (this.#observedUnavailableItem && this.#availabilityObserver) {
      this.#availabilityObserver.unobserve(this.#observedUnavailableItem);
    }

    this.#observedUnavailableItem = firstUnavailable;

    if (!firstUnavailable) return;

    if (!this.#availabilityObserver) {
      this.#availabilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.triggerNextPage();
            }
          }
        },
        { rootMargin: '800px' }
      );
    }

    this.#availabilityObserver.observe(firstUnavailable);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
